'use client';
import PropTypes from 'prop-types';
import * as React from 'react';
import warning from 'tiny-warning';
import { CollapsibleContext } from './collapsible.context.js';
import { useAccordionContext } from '../accordion/accordion.context.js';
import { createComponent } from '../core/create-component.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * Whether or not the collapsible is open, this makes the component controlled
   */ expanded: PropTypes.bool,
    /**
   * The event fired when the collapsible's expanded state changes
   */ onRequestExpandedChange: PropTypes.func,
    /**
   * Used to unset the left and right padding of the Collapsible
   * @default false
   */ fullBleed: PropTypes.bool,
    /**
   * The default expanded state of the Collapsible - used for uncontrolled components
   */ defaultExpanded: PropTypes.bool,
    /**
   * Used when a collapsible is used inside of an Accordion for tracking the open state of each panel
   */ collapsibleId: PropTypes.string,
    /**
   * Whether or not the collapsible can be expanded or collapsed
   */ disabled: PropTypes.bool
};
const Collapsible = createComponent({
    displayName: 'Collapsible',
    root: 'div',
    propTypes,
    requiredStyleKeys: SWAN_STYLE_KEY_MAP.accordion,
    propConfig: {
        derivedProps (props) {
            const { expanded: controlledExpanded, onRequestExpandedChange: controlledRequestExpandedChange, defaultExpanded = false, collapsibleId } = props;
            const [contentId, setContentId] = React.useState();
            const accordionContext = useAccordionContext();
            const accordionMode = accordionContext !== undefined;
            // when used inside of an Accordion, we need to manage the Collapsible's state at the root (to support `multiple` behavior)
            warning(!accordionMode || controlledExpanded === undefined && controlledRequestExpandedChange === undefined, 'When a Collapsible is used inside of an Accordion, you must provide the expanded-state to the Accordion component rather than directly to the Collapsible');
            warning(!(accordionMode && collapsibleId === undefined), 'When a Collapsible is used inside of an Accordion, the Collapsible must have a `collapsibleId`');
            const [localExpanded, localOnRequestExpandedChange] = useControllableValue({
                value: controlledExpanded,
                onChange: controlledRequestExpandedChange || undefined,
                defaultValue: defaultExpanded,
                defaultOnChange: (setUncontrolledExpanded, expanded)=>setUncontrolledExpanded(expanded)
            });
            let expanded = !!localExpanded;
            let onRequestExpandedChange = localOnRequestExpandedChange;
            if (accordionMode) {
                // accordionMode implies that accordionContext and collapsibleId are defined
                expanded = !!accordionContext.expandedCollapsibleMap[collapsibleId];
                onRequestExpandedChange = (isExpanded)=>{
                    accordionContext.requestExpandedChange(collapsibleId, isExpanded);
                };
            }
            return {
                expanded,
                onRequestExpandedChange,
                contentId,
                setContentId,
                purpose: accordionContext && accordionContext.purpose
            };
        },
        processProps (props, derivedProps) {
            return _object_spread_props(_object_spread({}, props), {
                'aria-disabled': !!props.disabled,
                as: props.as || props.component || (derivedProps.purpose === 'list' ? 'li' : 'div')
            });
        },
        classNames (props) {
            const { fullBleed = false, disabled } = props;
            const classNameSet = new Set([
                'swan-vanilla-ignore',
                'swan-collapsible'
            ]);
            if (disabled) classNameSet.add('swan-collapsible-disabled');
            if (fullBleed) classNameSet.add('swan-collapsible-full-bleed');
            return classNameSet;
        },
        wrapRoot ({ disabled }, derivedProps, element) {
            const { expanded, onRequestExpandedChange, contentId, setContentId } = derivedProps;
            return /*#__PURE__*/ React.createElement(CollapsibleContext.Provider, {
                value: {
                    expanded,
                    onRequestExpandedChange,
                    contentId,
                    setContentId,
                    disabled
                }
            }, element);
        }
    }
});

export { Collapsible };
